import React, { ComponentProps, VFC, useCallback, useId } from 'react';

import { Box, Container, Icon, IconButton, Text } from '@fhs-legacy/universal-components';
import LoadingAnimation from 'components/loading-animation';
import { theme } from 'styles/configure-theme';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

interface IStoreActionsButtonProps {
  accessibilityDescribedBy?: string;
  accessibilityLabel: string;
  disabled?: boolean;
  icon: ComponentProps<typeof Icon>['variant'];
  loading?: boolean;
  onPress?: () => void;
  text: string;
  testID?: string;
}

export const StoreActionsButton: VFC<IStoreActionsButtonProps> = ({
  accessibilityDescribedBy,
  accessibilityLabel,
  disabled = false,
  onPress = () => {},
  icon,
  text,
  loading = false,
  testID,
}) => {
  const storeActionButtonId = useId();

  const handlePress = useCallback(() => {
    if (disabled) {
      return;
    }
    onPress();
  }, [disabled, onPress]);

  return (
    <Container
      testID="store-action-button-container"
      alignItems="center"
      marginY={0}
      marginX={4}
      accessible={false}
      _web={{ role: 'presentation' } as React.ComponentProps<typeof Container>['_web']}
    >
      {loading ? (
        <Box
          size="$13.5"
          justifyContent="center"
          alignContent="center"
          borderRadius="full"
          position="relative"
          bg={theme.token('background-button-primary-default')}
        >
          <LoadingAnimation size="sm" reversed />
        </Box>
      ) : (
        <IconButton
          variant="unstyled"
          testID={testID}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          accessibilityDescribedBy={accessibilityDescribedBy}
          accessibilityState={disabled ? { disabled: true } : undefined}
          focusable={true}
          id={storeActionButtonId}
          isDisabled={false}
          onPress={handlePress}
          backgroundColor={
            loading || disabled
              ? theme.token('background-checkbox-disabled')
              : theme.token('background-button-primary-default')
          }
          borderRadius="full"
          position="relative"
          boxSize="$13.5"
          alignItems="center"
          justifyContent="center"
          hitSlop={10}
          padding="0"
          _icon={{ size: '9' }}
          icon={
            <Icon
              variant={icon}
              color={disabled ? theme.token('text-disabled') : theme.token('icon-button-primary')}
              {...hiddenAccessibilityPlatformProps}
            />
          }
          _web={
            disabled
              ? ({
                  cursor: 'not-allowed',
                  tabIndex: 0,
                  role: 'button',
                  'aria-disabled': true,
                  _hover: {
                    cursor: 'not-allowed',
                    backgroundColor: theme.token('background-checkbox-disabled'),
                  },
                } as React.ComponentProps<typeof IconButton>['_web'])
              : ({
                  _hover: {
                    cursor: 'pointer',
                    backgroundColor: theme.token('background-button-primary-hover'),
                  },
                } as React.ComponentProps<typeof IconButton>['_web'])
          }
          {...(disabled
            ? {}
            : {
                _pressed: {
                  backgroundColor: theme.token('background-button-primary-hover'),
                  style: {
                    transform: [{ scale: 0.95 }],
                  },
                },
              })}
        />
      )}

      <Text
        accessible={false}
        importantForAccessibility="no"
        fontFamily="heading"
        testID="store-action-text"
        color={loading || disabled ? theme.token('text-disabled') : theme.token('text-default')}
        marginTop="$1"
        marginBottom="$2"
        fontSize="md"
        _web={{ 'aria-hidden': true } as React.ComponentProps<typeof Text>['_web']}
      >
        {text}
      </Text>
    </Container>
  );
};
